import { useState } from 'react';
import clsx from 'clsx';
import { AmountUtils, CurrencyUtils, FormatUtils, PaletteUtils } from '@shared/utils';
import type { AccountResponse } from '@shared/api/rest/accounts';
import { Button, ColorsEnum, Icon, Text } from '@shared/widgets';
import ChevronIcon from '@assets/icons/chevron.svg';
import styles from '../../styles/accountPaymentTile.module.scss';

interface Props {
  account: AccountResponse;
  mainAccount?: AccountResponse;
  subAccounts: AccountResponse[];
  accounts?: boolean;
  currency?: string;
  selectedAccountId?: number;
  isOrphanSub?: boolean;
  onAccountSwitch?: (accountId: number) => void;
}

export const AccountPaymentSmallTile = (props: Props) => {
  const [options, setOptions] = useState<boolean>(true);
  const handleArrowClick = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setOptions(!options);
  };

  const accountDetails = [
    { label: 'IBAN', value: props.account.iban },
    { label: 'Account number', value: props.account.accountNumber },
    ...(!props.accounts ? [{ label: 'Account Status', value: props.account.status }] : []),
  ];

  return (
    <div className={clsx('h-full flex flex-col', styles['account-payment-card'])}>
      <div className={clsx('flex flex-row justify-between px-5 py-3', styles['bg-account-payment'], PaletteUtils.isVcfcPalette && styles.vcfc)}>
        <div className='flex flex-col'>
          <Text weight='semibold' color={ColorsEnum.White}>
            {props.account.name || FormatUtils.formatAccountNumber(props.account.accountNumber)}
          </Text>
          <Text size='xs' color={ColorsEnum.Gray} isUppercase>
            {props.account.productName}
          </Text>
        </div>
      </div>
      <div className={clsx(`h-full flex flex-col ${!props.accounts ? 'gap-3' : ''}  bg-white dark:bg-soft-black p-5`)}>
        <Text size='3xl'>{AmountUtils.transformToAmountWithCurrency(props.account.availableBalance ?? 0, props.account.currency)}</Text>
        {!props.accounts && (
          <div className='flex gap-2'>
            <Button
              color='icon'
              disabled={props.mainAccount === undefined}
              onClick={() => props.onAccountSwitch!(props.account.mainAccId === null ? props.account.id : props.account.mainAccId)}
              className={clsx(
                'border uppercase !rounded text-xs py-2 px-4 font-semibold',
                props.account.mainAccId === null
                  ? props.selectedAccountId === props.account.id
                    ? 'bg-primary text-white'
                    : '!text-light-gray border-light-gray '
                  : ' !text-light-gray border-light-gray ',
              )}
            >
              {CurrencyUtils.getSymbolByCode(props.mainAccount ? props.mainAccount.currency : '')} Main Account
            </Button>
            {props.subAccounts.map((subAccount) => (
              <Button
                color='icon'
                key={subAccount.id}
                onClick={() => props.onAccountSwitch!(subAccount.id)}
                className={clsx(
                  'border uppercase  !rounded text-xs py-2 px-4 font-semibold',
                  props.selectedAccountId === subAccount.id ? 'bg-primary text-white' : '!text-light-gray border-light-gray ',
                )}
              >
                {CurrencyUtils.getSymbolByCode(subAccount.currency)} Sub Account
              </Button>
            ))}
          </div>
        )}
        <div className={!props.accounts ? 'flex gap-6' : 'flex flex-col gap-2 mt-2'}>
          {accountDetails.map((item, index) => (
            <div key={index}>
              <Text size='xs'>{item.label}</Text>
              <Text size='xs' weight='semibold'>
                {item.value}
              </Text>
            </div>
          ))}
        </div>
        {props.accounts && props.subAccounts?.length > 0 && (
          <div className='h-full'>
            <div className='flex items-center justify-between min-h-12 px-0 py-4 cursor-pointer'>
              <Text size='xs'>Other currencies</Text>
              <div className='flex gap-1'>
                {props.subAccounts &&
                  props.subAccounts.length > 0 &&
                  props.subAccounts.map((c) => (
                    <div key={c.id} className='flex items-center font-semibold text-xs px-1.5 border text-light-gray rounded h-5'>
                      {CurrencyUtils.getSymbolByCode(c.currency)}
                    </div>
                  ))}
                <span
                  onClick={handleArrowClick}
                  className={clsx(
                    'text-slate dark:text-gray w-4 h-4 flex-none text-xl flex items-center justify-center text-center mx-2 transition ease-out',
                    options ? 'rotate-90' : '-rotate-90',
                  )}
                >
                  <Icon icon={<ChevronIcon />} />
                </span>
              </div>
            </div>
            <div className={clsx(!options ? 'hidden' : 'block pt-3')}>
              {props.subAccounts?.length > 0 && (
                <div>
                  {props.subAccounts.map((subAccount) => (
                    <div key={subAccount.id} className='flex gap-3 items-center bg-ice dark:bg-slate px-3 py-2 mb-2'>
                      <div className='border justify-center uppercase text-primary rounded h-full flex items-center px-2'>
                        <span className='text-base mr-2.5 ml-1'>{CurrencyUtils.getSymbolByCode(subAccount.currency)}</span>
                        <Text weight='semibold' size='xs' isUppercase color={ColorsEnum.Primary} className='mt-0.5'>
                          Sub
                        </Text>
                      </div>
                      <div>
                        <Text size='xs'>Account balance</Text>
                        <Text size='xs' weight='semibold'>
                          {AmountUtils.transformToAmountWithCurrency(subAccount.availableBalance ?? 0, subAccount.currency)}
                        </Text>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
